import { Suspense, useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";

import { useLogout, useSummary } from "../../api/hooks.ts";
import { initials, labelRole } from "../../domain/format.ts";
import { Icon, type IconName } from "../../ui/Icon.tsx";
import { LoadingPart } from "../../ui/lazyPart.tsx";
import { useTheme } from "../../ui/theme.ts";
import { QuickAdd } from "../../ui/QuickAdd.tsx";
import { ScrollTop } from "../../ui/ScrollTop.tsx";
import { usePermissions, type Permissions } from "../auth/permissions.ts";
import { NotificationBell } from "./NotificationBell.tsx";
import { SECTION_PARAM, sectionFrom, sectionUrl } from "../library/section.ts";
import { OwnerRibbon, OwnerSwitch } from "./OwnerSwitch.tsx";

/**
 * El armazon de la plantilla (ADR 0003): sidebar fijo en escritorio, barra
 * inferior con boton central en movil, y una topbar que dice siempre donde
 * estas.
 *
 * En movil el sidebar se convierte en cajon y "Nueva" sigue siendo el destino
 * central y el mas grande (SPEC 15.4), porque el registro se hace con el
 * telefono en una mano.
 */

type Entry = {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /** Lo que existe se navega; lo que aun no, se ve y no se pulsa. */
  soon?: string;
  /**
   * Que permiso hace falta para que el destino aparezca. Sin esto, el menu
   * ofreceria a un Mentor un formulario que va a responder 403, y eso se lee
   * como una aplicacion que falla, no como un permiso que no tiene.
   *
   * ESCONDER NO ES AUTORIZAR: quien escriba la direccion a mano llega a la
   * pantalla, y ahi el que dice no es el servidor (ver `auth/permissions.ts`).
   */
  needs?: (permissions: Permissions) => boolean;
};

type Group = { title?: string; items: Entry[] };

const NAV: Group[] = [
  {
    items: [
      { to: "/", label: "Panel", icon: "home", end: true },
      { to: "/trades", label: "Bitacora", icon: "feed" },
      {
        to: "/trades/new",
        label: "Nueva operacion",
        icon: "plus",
        needs: (permissions) => permissions.canWriteTrades,
      },
    ],
  },
  {
    title: "Analisis",
    items: [
      { to: "/semana", label: "Revision semanal", icon: "calendar" },
      { to: "/metricas", label: "Metricas", icon: "chart" },
      { to: "/aprendizaje", label: "Aprendizaje", icon: "book" },
      { to: "/biblioteca", label: "Biblioteca", icon: "library" },
      /* Frases tiene pantalla propia desde que son cuatrocientas: en la
         Biblioteca serian una cuarta pestaña, y lo que el propietario pidio es
         pasarlas con el dedo, que necesita la pantalla entera. */
      { to: "/frases", label: "Frases", icon: "quote" },
    ],
  },
  {
    title: "Mantenimiento",
    items: [
      {
        to: "/usuarios",
        label: "Usuarios",
        icon: "users",
        needs: (permissions) => permissions.isAdministrator,
      },
      { to: "/instrumentos", label: "Indices y pares", icon: "layers" },
      { to: "/catalogos/brokers", label: "Brokers y listas", icon: "briefcase" },
      { to: "/catalogos/sugerencias", label: "Sugerencias", icon: "quote" },
    ],
  },
  {
    title: "Sistema",
    items: [{ to: "/settings", label: "Ajustes", icon: "settings" }],
  },
];

const PAGES: { match: RegExp; title: string; subtitle: string }[] = [
  { match: /^\/$/, title: "Panel", subtitle: "Lo que dicen tus numeros hasta hoy" },
  {
    match: /^\/trades\/new$/,
    title: "Nuevo trade",
    subtitle: "Todo en una pantalla, y el borrador se guarda solo",
  },
  {
    match: /^\/trades\/[^/]+$/,
    title: "Operacion",
    subtitle: "El plan, la ejecucion y el relato",
  },
  { match: /^\/trades$/, title: "Bitacora", subtitle: "Operaciones y notas, dia a dia" },
  {
    match: /^\/semana$/,
    title: "Revision semanal",
    subtitle: "Los siete dias, y lo que escribiste en ellos",
  },
  {
    match: /^\/metricas$/,
    title: "Metricas",
    subtitle: "Catorce formas de mirar las mismas operaciones",
  },
  {
    match: /^\/aprendizaje$/,
    title: "Aprendizaje",
    subtitle: "Las tres preguntas que suelen explicar un mes malo",
  },
  {
    match: /^\/frases$/,
    title: "Frases",
    subtitle: "Una a una, pasandolas con el dedo",
  },
  {
    match: /^\/biblioteca$/,
    title: "Biblioteca",
    subtitle: "Tus capturas, tus estudios y lo que guardas de las redes",
  },
  {
    match: /^\/instrumentos$/,
    title: "Indices y pares",
    subtitle: "El tamano de pip decide tus estadisticas",
  },
  {
    match: /^\/instrumentos\/[^/]+$/,
    title: "Simbolo",
    subtitle: "Todo lo que hiciste en este indice o par",
  },
  {
    match: /^\/catalogos\/sugerencias$/,
    title: "Sugerencias y frases",
    subtitle: "Lo que te propone el formulario y lo que rota en el panel",
  },
  {
    match: /^\/catalogos\//,
    title: "Brokers y listas",
    subtitle: "Lo que rellena los desplegables del formulario",
  },
  { match: /^\/settings$/, title: "Ajustes", subtitle: "Sesion, tema y datos" },
  {
    match: /^\/usuarios$/,
    title: "Usuarios",
    subtitle: "Quien puede entrar a la bitacora y con que permisos",
  },
];

export function AppShell(): ReactNode {
  const location = useLocation();
  /* Que pestaña de la Biblioteca se mira, para que el boton de Social Data de la
     barra de abajo se encienda solo en la suya. */
  const section = sectionFrom(new URLSearchParams(location.search).get(SECTION_PARAM));
  const navigate = useNavigate();
  const logout = useLogout();
  const summary = useSummary();
  const { theme, toggle } = useTheme();
  const permissions = usePermissions();

  const [menuOpen, setMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  /* La casilla de "Otra bitacora…" del Mentor vive entre el selector, que la
     abre, y la cinta, que la pinta; por eso el estado esta aqui, en el padre
     de los dos. */
  const [askingOwnerId, setAskingOwnerId] = useState(false);

  /* Al navegar se cierran los dos: dejarlos abiertos tapa la pantalla a la que
     acabas de llegar. */
  useEffect(() => {
    setMenuOpen(false);
    setSheetOpen(false);
  }, [location.pathname]);

  const page = PAGES.find((candidate) => candidate.match.test(location.pathname));
  const count = summary.data?.totalTrades;

  /* Un grupo que se queda sin destinos desaparece con su titulo: un
     "Mantenimiento" con nada debajo parece un menu roto. */
  const nav = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.needs === undefined || item.needs(permissions)),
  })).filter((group) => group.items.length > 0);

  const me = permissions.user;

  function salir(): void {
    logout.mutate(undefined, { onSuccess: () => navigate("/login", { replace: true }) });
  }

  return (
    <div className="app">
      <aside className={menuOpen ? "sidebar open" : "sidebar"}>
        <div className="sb-brand">
          <div className="brand">
            <span className="brand-mark">
              <Icon name="trend" />
            </span>
            <span>TradingJournal</span>
          </div>
        </div>

        <nav className="sb-nav" aria-label="Navegacion principal">
          {nav.map((group, index) => (
            <div key={group.title ?? index}>
              {group.title !== undefined && <div className="sb-group">{group.title}</div>}
              {group.items.map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  count={item.to === "/trades" ? count : undefined}
                />
              ))}
            </div>
          ))}
        </nav>

        {/* Quien esta dentro, con su rol: en un sistema compartido, "sesion
            abierta" no dice lo que hace falta saber antes de escribir en una
            bitacora, que es de quien es la bitacora. */}
        <div className="sb-user">
          <span className="avatar">{initials(me?.displayName)}</span>
          <div className="u-meta">
            <b>{me?.displayName ?? "Tu bitacora"}</b>
            <span>{me === undefined ? "Sesion abierta" : labelRole(me.role)}</span>
          </div>
          <button
            type="button"
            className="icon-btn"
            title="Cerrar sesion"
            aria-label="Cerrar sesion"
            onClick={salir}
          >
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      <div
        className={menuOpen ? "scrim open" : "scrim"}
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
      />

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            id="menuBtn"
            className="icon-btn"
            aria-label="Abrir el menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" />
          </button>

          <div className="tb-title">
            <h2>{page?.title ?? "TradingJournal"}</h2>
            <span>{page?.subtitle ?? "Este journal no mide dinero. Mide habilidad."}</span>
          </div>

          <div className="tb-actions">
            {/* De quien es la bitacora que se mira. Solo lo ven Administrador y
                Mentor; para los demas no pinta nada (ver `OwnerSwitch`). */}
            <OwnerSwitch askingId={askingOwnerId} onAskId={setAskingOwnerId} />
            {/* La campana va ANTES del tema y de "Nueva": su panel se abre hacia
                abajo pegado al borde derecho, y desde el centro de la barra
                apuntaria a un sitio donde no esta el boton. */}
            <NotificationBell />
            <button
              type="button"
              className="icon-btn"
              onClick={toggle}
              title={theme === "dark" ? "Pasar al tema claro" : "Pasar al tema oscuro"}
              aria-label={theme === "dark" ? "Pasar al tema claro" : "Pasar al tema oscuro"}
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} />
            </button>
            {permissions.canWriteTrades && (
              <NavLink className="btn primary sm desktop-only" to="/trades/new">
                <Icon name="plus" />
                Nueva
              </NavLink>
            )}
          </div>
        </header>

        {/* La cinta va bajo la topbar y fuera de `.content`: no es parte de
            ninguna pantalla, es el contexto de todas mientras dure. */}
        <OwnerRibbon askingId={askingOwnerId} onAskId={setAskingOwnerId} />

        <main className="content">
          <div className="view" key={location.pathname}>
            {/* Las pantallas que viajan aparte (`lazyPart`) se esperan AQUI, dentro
                del armazon: la barra lateral y la cabecera se quedan, y solo el
                contenido ensena el esqueleto mientras llega. */}
            <Suspense fallback={<LoadingPart />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {/* Fuera del <main> y de la barra: es flotante y no pertenece a ninguno
          de los dos. Aqui lo ve cualquier pantalla sin que cada una lo monte. */}
      <ScrollTop />

      <nav className="bottomnav" aria-label="Navegacion principal">
        <BottomItem to="/" label="Panel" icon="home" end />
        <BottomItem to="/trades" label="Bitacora" icon="feed" />
        {/* El Mentor no registra operaciones, asi que no lleva el boton central:
            es el destino mas grande de la barra y seria el 403 mas facil de
            pulsar de toda la aplicacion. */}
        {permissions.canWriteTrades && (
          <div className="bn-slot">
            {/* La nota se abre navegando a la Bitacora con `?nota=nueva`, no
                montando otro modal aqui: el de notas vive alli con su lista y su
                cache, y duplicarlo serian dos sitios donde arreglar el mismo
                fallo. Es el mismo mecanismo que usa la campana con
                `?pendientes=`. */}
            <QuickAdd onNote={() => navigate("/trades?nota=nueva")} />
          </div>
        )}
        {/* SOCIAL DATA EN LUGAR DE SIMBOLOS, pegado al boton central. Lo pidio
            el propietario por uso: "yo veo mas social data, bitacora y panel,
            pon a simbolos en el boton mas". Un simbolo se consulta cuando se
            crea o se corrige; Social Data se abre a diario. La barra de abajo es
            el sitio mas caro de la pantalla y lo ocupa lo que se usa. */}
        <BottomItem
          to={sectionUrl("social")}
          label="Social Data"
          icon="bookmark"
          /* El `isActive` de NavLink mira SOLO la ruta, y las tres pestañas de la
             Biblioteca comparten `/biblioteca`: sin esto, el boton saldria
             encendido tambien en Capturas y en Estudios. */
          active={location.pathname === "/biblioteca" && section === "social"}
        />
        <button
          type="button"
          className="bn-item"
          aria-expanded={sheetOpen}
          onClick={() => setSheetOpen((open) => !open)}
        >
          <Icon name="more" />
          Mas
        </button>
      </nav>

      <div className={sheetOpen ? "sheet open" : "sheet"} role="dialog" aria-label="Mas destinos">
        <div className="grab" />
        {nav
          .flatMap((group) => group.items)
          .filter((item) => !BOTTOM.includes(item.to))
          .map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        <button type="button" className="nav-item" onClick={salir}>
          <Icon name="logout" />
          <span>Cerrar sesion</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Lo que ya esta en la barra inferior no se repite en la hoja de "Mas".
 *
 * `/instrumentos` SALIO de esta lista cuando Social Data le quito su sitio en la
 * barra: tiene que volver a aparecer en la hoja, o quedaria sin ninguna puerta en
 * el movil. `/biblioteca` nunca estuvo y sigue sin estar, porque el boton de
 * abajo lleva a UNA de sus tres pestañas y la Biblioteca entera es otro destino.
 */
const BOTTOM = ["/", "/trades", "/trades/new"];

function NavItem(props: { item: Entry; count?: number | undefined }): ReactNode {
  const { item } = props;

  if (item.soon !== undefined) {
    return (
      <span className="nav-item soon" aria-disabled="true">
        <Icon name={item.icon} />
        <span>{item.label}</span>
        <span className="cnt">{item.soon}</span>
      </span>
    );
  }

  return (
    <NavLink
      className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
      to={item.to}
      end={item.end === true}
    >
      <Icon name={item.icon} />
      <span>{item.label}</span>
      {props.count !== undefined && <span className="cnt">{props.count}</span>}
    </NavLink>
  );
}

function BottomItem(props: {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /**
   * Manda sobre el `isActive` de NavLink, que mira solo la ruta. Lo necesita el
   * destino que se distingue por la query -- Social Data es `/biblioteca` con un
   * parametro --, donde la ruta sola encenderia el boton en las tres pestañas.
   */
  active?: boolean;
}): ReactNode {
  return (
    <NavLink
      className={({ isActive }) =>
        (props.active ?? isActive) ? "bn-item active" : "bn-item"
      }
      to={props.to}
      end={props.end === true}
    >
      <Icon name={props.icon} />
      {props.label}
    </NavLink>
  );
}
